import type { ComponentType } from "react";

import { ArrowBackOutlined } from "@aircall/icons";
import { Flex, Menu, MenuItem, Spacer, Typography } from "@aircall/tractor";
import { useSearchParams } from "react-router-dom";

import { PlaygroundAccordion } from "../PlaygroundAccordion/PlaygroundAccordion";
import { PlaygroundCsatDriver } from "../PlaygroundCsatDriver";
import { PlaygroundCsatScore } from "../PlaygroundCsatScore";
import { PlaygroundCsatScoreRow } from "../PlaygroundCsatScoreRow";
import { PlaygroundEvaluationField } from "../PlaygroundEvaluationField/PlaygroundEvaluationField";
import { PlaygroundEvaluationQuestionSummaryRating } from "../PlaygroundEvaluationQuestionSummaryRating";
import { PlaygroundFeedbacks } from "../PlaygroundFeedbacks";
import { PlaygroundInputChoices } from "../PlaygroundInputChoices/PlaygroundInputChoices";
import { PlaygroundPercentBadge } from "../PlaygroundPercentBadge";
import { PlaygroundReviewEvaluationField } from "../PlaygroundReviewEvaluationField/PlaygroundReviewEvaluationField";
import { PlaygroundReviewHeader } from "../PlaygroundReviewHeader/PlaygroundReviewHeader";
import { PlaygroundReviewNotes } from "../PlaygroundReviewNotes/PlaygroundReviewNotes";
import { PlaygroundReviewScorecards } from "../PlaygroundReviewScorecards/PlaygroundReviewScorecards";
import { PlaygroundReviewToast } from "../PlaygroundReviewToast/PlaygroundReviewToast";
import { PlaygroundScoreBadge } from "../PlaygroundScoreBadge";
import { PlaygroundSectionCallEvaluation } from "../PlaygroundSectionCallEvaluation";
import { PlaygroundSectionCallType } from "../PlaygroundSectionCallType";
import { PlaygroundSectionCallUuid } from "../PlaygroundSectionCallUuid";
import { PlaygroundSectionCsat } from "../PlaygroundSectionCsat";
import { PlaygroundSectionCustomSummary } from "../PlaygroundSectionCustomSummary";
import { PlaygroundSectionDisconnectedBy } from "../PlaygroundSectionDisconnectedBy";
import { PlaygroundSectionKeyTopics } from "../PlaygroundSectionKeyTopics";
import { PlaygroundSectionNextActions } from "../PlaygroundSectionNextActions";
import { PlaygroundSectionNotes } from "../PlaygroundSectionNotes";
import { PlaygroundSectionNumber } from "../PlaygroundSectionNumber";
import { PlaygroundSectionParticipants } from "../PlaygroundSectionParticipants";
import { PlaygroundSectionPlaybook } from "../PlaygroundSectionPlaybook";
import { PlaygroundSectionSentiment } from "../PlaygroundSectionSentiment";
import { PlaygroundSectionTags } from "../PlaygroundSectionTags";
import { PlaygroundSectionTalkRatio } from "../PlaygroundSectionTalkRatio";
import { PlaygroundSectionTiming } from "../PlaygroundSectionTiming";
import { PlaygroundSlider } from "../PlaygroundSlider/PlaygroundSlider";
import { PlaygroundTagDisplay } from "../PlaygroundTagDisplay/PlaygroundTagDisplay";
import { PlaygroundVirtualAgentTag } from "../PlaygroundVirtualAgentTag/PlaygroundVirtualAgentTag";

const COMPONENTS: Record<string, ComponentType> = {
  Accordion: PlaygroundAccordion,
  PercentBadge: PlaygroundPercentBadge,
  CsatScore: PlaygroundCsatScore,
  CsatScoreRow: PlaygroundCsatScoreRow,
  CsatDriver: PlaygroundCsatDriver,
  EvaluationField: PlaygroundEvaluationField,
  Feedbacks: PlaygroundFeedbacks,
  InputChoices: PlaygroundInputChoices,
  ReviewNotesAvatar: PlaygroundReviewNotes,
  ReviewScorecardSelector: PlaygroundReviewScorecards,
  ReviewEvaluationField: PlaygroundReviewEvaluationField,
  ReviewCreateFormHeader: PlaygroundReviewHeader,
  ReviewPublishToast: PlaygroundReviewToast,
  VirtualAgentTag: PlaygroundVirtualAgentTag,
  EvaluationQuestionSummaryRating: PlaygroundEvaluationQuestionSummaryRating,
  ScoreBadge: PlaygroundScoreBadge,
  Slider: PlaygroundSlider,
  TagDisplay: PlaygroundTagDisplay,
  SectionCallEvaluation: PlaygroundSectionCallEvaluation,
  SectionCallType: PlaygroundSectionCallType,
  SectionCallUuid: PlaygroundSectionCallUuid,
  SectionCsat: PlaygroundSectionCsat,
  SectionCustomSummary: PlaygroundSectionCustomSummary,
  SectionDisconnectedBy: PlaygroundSectionDisconnectedBy,
  SectionKeyTopics: PlaygroundSectionKeyTopics,
  SectionNextActions: PlaygroundSectionNextActions,
  SectionNotes: PlaygroundSectionNotes,
  SectionNumber: PlaygroundSectionNumber,
  SectionParticipants: PlaygroundSectionParticipants,
  SectionPlaybook: PlaygroundSectionPlaybook,
  SectionSentiment: PlaygroundSectionSentiment,
  SectionTags: PlaygroundSectionTags,
  SectionTalkRatio: PlaygroundSectionTalkRatio,
  SectionTiming: PlaygroundSectionTiming,
};

export function Playground() {
  const [searchParams, setSearchParams] = useSearchParams();
  const componentName = searchParams.get("component");
  const Component = componentName ? COMPONENTS[componentName] : null;

  if (!Component) {
    const componentKeys = Object.keys(COMPONENTS);
    return (
      <Flex
        background="white"
        p="s"
        flexDirection="column"
        gap="s"
        h="100%"
        overflow="auto"
      >
        {componentKeys.length > 0 ? (
          <Menu>
            {componentKeys.map((name) => (
              <MenuItem
                key={name}
                onClick={() => setSearchParams({ component: name })}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
        ) : (
          <Typography>No components registered yet.</Typography>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      background="white"
      p="s"
      gap="m"
      h="100%"
      overflow="auto"
    >
      <Spacer
        gap="xxs"
        alignItems="center"
        cursor="pointer"
        onClick={() => setSearchParams({})}
        data-test="playground-back"
      >
        <ArrowBackOutlined size={16} />
        <Typography variant="bodyMediumS">Back to components</Typography>
      </Spacer>
      <Component />
    </Flex>
  );
}
